/**
 * The power course's levels, on the Wren Valley grid (docs/grid.md).
 *
 * A level is data: where the grid starts, what the learner may change, a
 * brief, and goals checked against the day the learner's settings produce.
 * Graded on the state left behind, the rule the software course is built
 * on: nothing here looks at what was clicked, only at what the grid does.
 *
 * English only for now, on the owner's instruction (2026-10-16), so the
 * words live here rather than in the typed string catalogue.
 */
import {
  BUSES,
  DEFAULT_SETTINGS,
  HUB_DEADLINE,
  HUB_MWH,
  LINES,
  STEPS,
  STREETS,
  TRANSFORMERS,
  clock,
  cost,
  hubHours,
  hubOnTime,
  runDay,
  type DayResult,
  type GridSettings,
} from './model';

export const V_MIN = 0.95;
export const V_MAX = 1.05;

export interface Controls {
  oltc: boolean;
  taps: boolean;
  conductors: boolean;
  switches: boolean;
  hub: boolean;
}

export interface Goal {
  id: string;
  text: string;
}

export interface GoalResult {
  id: string;
  ok: boolean;
  /** What happened, in a sentence: the worst moment when it failed. */
  detail: string;
}

export interface GridLevel {
  id: string;
  n: number;
  title: string;
  /** Who sent the job and when, like a ticket header. */
  from: string;
  brief: string[];
  hint: string;
  goals: Goal[];
  budget: number;
  controls: Controls;
  start: GridSettings;
  debrief: string[];
  leavesOut: string;
}

const busName = (id: string) => BUSES.find((b) => b.id === id)?.name ?? id;
const branchName = (id: string) => LINES.find((l) => l.id === id)?.name ?? TRANSFORMERS.find((t) => t.id === id)?.name ?? id;
const money = (usd: number) => `$${Math.round(usd).toLocaleString('en-US')}`;

/** Every street within limits all day: the worst moment if not. */
function voltages(day: DayResult): GoalResult {
  if (!day.ok) return { id: 'voltage', ok: false, detail: 'The grid could not be solved; see the supply goal.' };
  let worst: { street: string; v: number; step: number; over: number } | null = null;
  for (let k = 0; k < STEPS; k++) {
    const snap = day.steps[k]!;
    for (const st of STREETS) {
      const v = snap.v[st];
      const over = v < V_MIN ? V_MIN - v : v > V_MAX ? v - V_MAX : 0;
      if (over > 0 && (!worst || over > worst.over)) worst = { street: st, v, step: k, over };
    }
  }
  if (!worst) return { id: 'voltage', ok: true, detail: 'Every street stayed between 0.95 and 1.05 all day.' };
  const which = worst.v < V_MIN ? `lowest allowed ${V_MIN.toFixed(2)}` : `highest allowed ${V_MAX.toFixed(2)}`;
  return {
    id: 'voltage',
    ok: false,
    detail: `${busName(worst.street)} reached ${worst.v.toFixed(3)} at ${clock(worst.step)} (${which}).`,
  };
}

/** No line or transformer over its rating at any moment. */
function loadings(day: DayResult): GoalResult {
  if (!day.ok) return { id: 'loading', ok: false, detail: 'The grid could not be solved; see the supply goal.' };
  let worst: { id: string; load: number; step: number } | null = null;
  for (let k = 0; k < STEPS; k++) {
    for (const [id, load] of Object.entries(day.steps[k]!.loading)) {
      if (load > 1 && (!worst || load > worst.load)) worst = { id, load, step: k };
    }
  }
  if (!worst) return { id: 'loading', ok: true, detail: 'Nothing ran over its rating.' };
  return {
    id: 'loading',
    ok: false,
    detail: `${branchName(worst.id)} ran at ${Math.round(worst.load * 100)}% of its rating at ${clock(worst.step)}.`,
  };
}

/** Everyone connected, through exactly one path. */
function supply(day: DayResult): GoalResult {
  if (day.ok) return { id: 'supply', ok: true, detail: 'Every street stayed connected.' };
  if (day.reason === 'loop')
    return {
      id: 'supply',
      ok: false,
      detail: 'Two paths to the same street. Distribution grids run radial, one path each, so the protection knows which way a fault is.',
    };
  if (day.reason === 'island') {
    const streets = (day.buses ?? []).filter((b) => (STREETS as readonly string[]).includes(b)).map(busName);
    return { id: 'supply', ok: false, detail: `Cut off from the supply: ${streets.join(', ') || 'part of the network'}.` };
  }
  return { id: 'supply', ok: false, detail: `The voltage collapsed at ${String(Math.floor(day.hour ?? 0)).padStart(2, '0')}:00: the network cannot carry that load that way.` };
}

function budget(s: GridSettings, limit: number): GoalResult {
  const spent = cost(s);
  return spent <= limit
    ? { id: 'budget', ok: true, detail: `${money(spent)} of ${money(limit)}.` }
    : { id: 'budget', ok: false, detail: `${money(spent)} is over the ${money(limit)} budget.` };
}

function hub(s: GridSettings): GoalResult {
  if (s.hubMw === null) return { id: 'hub', ok: false, detail: 'The hub is not connected.' };
  const hours = hubHours(s.hubMw);
  const done = (17 + hours) % 24;
  const at = `${String(Math.floor(done)).padStart(2, '0')}:${String(Math.round((done % 1) * 60)).padStart(2, '0')}`;
  return hubOnTime(s.hubMw)
    ? { id: 'hub', ok: true, detail: `At ${s.hubMw} MW the cars are full at ${at}, before ${HUB_DEADLINE}:00.` }
    : { id: 'hub', ok: false, detail: `At ${s.hubMw} MW the cars are not full until ${at}.` };
}

const GOAL_TEXT = {
  voltage: 'Every street stays between 0.95 and 1.05 per unit, all day.',
  loading: 'No line or transformer runs over its rating.',
  supply: 'Every street stays connected, by one path.',
  budget: (usd: number) => (usd === 0 ? 'Spend nothing: this is a settings change.' : `Spend no more than ${money(usd)}.`),
  hub: `The hub delivers its ${HUB_MWH} MWh before ${HUB_DEADLINE}:00.`,
};

export const LEVELS: GridLevel[] = [
  {
    id: 'dim-lights',
    n: 1,
    title: 'Dim lights at 7 pm',
    from: 'Customer service, Monday 09:12',
    brief: [
      'Residents of Elm Street say their lights dim every evening around seven, and two heat pumps have shut themselves off on low voltage. Nobody else has complained.',
      'Elm Street is at the far end of Feeder A, about 8.5 km of overhead line from the substation. Every kilometre of line drops a little voltage, and it drops most when everyone is home and cooking.',
    ],
    hint: 'Run the day first and watch the colours. Click a street to see its voltage through the day; click a transformer to change its tap.',
    goals: [
      { id: 'voltage', text: GOAL_TEXT.voltage },
      { id: 'loading', text: GOAL_TEXT.loading },
      { id: 'supply', text: GOAL_TEXT.supply },
    ],
    budget: 0,
    controls: { oltc: true, taps: true, conductors: false, switches: false, hub: false },
    start: structuredClone(DEFAULT_SETTINGS),
    debrief: [
      "Elm Street's transformer has taps: a few extra turns of wire that boost its street's voltage by 2.5% or 5%. One step lifts the evening dip back over 0.95. Two steps push the quiet night, when the line barely drops anything, over 1.05.",
      'Raising the whole substation one step also works, but it lifts every street, including the ones that were fine, and leaves less room before the night limit. The local fix targets the street with the problem. Real grids use both, and a real substation tap changer moves by itself through the day.',
    ],
    leavesOut: 'Unbalanced phases, the cable from each transformer to the houses, and the automatic tap changer at the substation.',
  },
  {
    id: 'ev-hub',
    n: 2,
    title: 'The charging hub',
    from: 'New connections, Wednesday 14:40',
    brief: [
      'A bus and taxi company is opening a charging hub beside Station Square, on Feeder B. They have asked for 3 MW from 17:00, and they need 12 MWh in the vehicles before the morning shift at 07:00.',
      'Feeder B was built for homes. Connect the hub without overloading anything, keep every street within limits, and stay within the budget the board approved.',
    ],
    hint: 'Run the day to see what 3 MW does. You can upgrade a line section, move the open point between the feeders, or agree a lower charging power with the hub: it charges for longer at a lower rate.',
    goals: [
      { id: 'voltage', text: GOAL_TEXT.voltage },
      { id: 'loading', text: GOAL_TEXT.loading },
      { id: 'supply', text: GOAL_TEXT.supply },
      { id: 'hub', text: GOAL_TEXT.hub },
      { id: 'budget', text: GOAL_TEXT.budget(150_000) },
    ],
    budget: 150_000,
    controls: { oltc: true, taps: true, conductors: true, switches: true, hub: true },
    start: { ...structuredClone(DEFAULT_SETTINGS), taps: { ...DEFAULT_SETTINGS.taps, elm: 3 }, hubMw: 3 },
    debrief: [
      'Two answers work. A heavier first section of Feeder B carries the full 3 MW for $120,000. Or the hub charges at 2.5 MW or less: the same 12 MWh, spread over more of the night, when the town is asleep and the line has room. That second answer costs nothing, and utilities now offer exactly this as "flexible connection" or "smart charging".',
      'Moving the hub onto Feeder A through the tie fails badly: Feeder A is long and already carries Elm Street, so the voltage at its end collapses. Where a load connects matters as much as how big it is.',
    ],
    leavesOut: 'Fault levels and protection settings for the new connection, the hub’s own transformer, and harmonics from the chargers.',
  },
];

export interface Verdict {
  ok: boolean;
  goals: GoalResult[];
  day: DayResult;
}

/** Run the learner's day and check every goal of the level. */
export function grade(level: GridLevel, s: GridSettings): Verdict {
  const day = runDay(s);
  const results: GoalResult[] = level.goals.map((g) => {
    switch (g.id) {
      case 'voltage':
        return voltages(day);
      case 'loading':
        return loadings(day);
      case 'supply':
        return supply(day);
      case 'budget':
        return budget(s, level.budget);
      case 'hub':
        return hub(s);
      default:
        return { id: g.id, ok: false, detail: 'Unknown goal.' };
    }
  });
  return { ok: results.every((r) => r.ok), goals: results, day };
}

export const LEVEL_IDS = LEVELS.map((l) => l.id);
